import { PedigreeFamily } from './Family.js';
import { config, isGameteDonorNode } from './config.js';
import {
  childPath,
  spousePath,
  twinGroupPath,
  twinApex,
  reproDescentPath,
  bioConnectorPath
} from './path.js';
import { executeLayoutV2 } from './layoutV2.js';
import { applyDisplacedSibshipLift } from './displacedSibshipLift.js';
import { firstClearSlot } from './utils.js';
import { refineCrossingsAwareV3 } from './crossingRefine.js';
import { detectAllSegmentIntersections } from './lineIntersection.js';
import { yFromRowUnits } from './rowUnits.js';

function createUnionId(id1, id2) {
  const [first, second] = [id1, id2].sort();
  return `union-${first}-${second}`;
}

// Build the render model (person nodes, union nodes, and edges) from a LAID-OUT
// family. Pure w.r.t. the family's virtualX/virtualY (re-callable): the crossing-aware
// refinement runs it repeatedly to score trial moves with the real detector, and
// pedigree() runs it once for the final output. Exported for that refinement + tests.
//
// B3a (scoringMode): the crossing-scoring hot path (crossingRefine + the reorder search
// call this hundreds of times per layout, then read ONLY the detector's crossing count
// from node x/y + edge structure) never touches the SVG `d` path strings or the spread
// clinical `person.data` fields — verified: lineIntersection's decompose/buildSegments/
// edgeNodeInfo derive geometry from node coords + edge.kind/from/to/laneY/twinIds only.
// When scoringMode is true we skip building those (the dominant per-call cost — see the
// perf profile: buildRenderModel was 3-5x the detector), yielding a byte-identical
// crossing/over/dog score. The FINAL, rendered model is ALWAYS built with scoringMode
// off (default), so goldens are unaffected; a parity test pins score-equality on/off.
export function buildRenderModel(
  pedigreeFamily,
  customPositions,
  scoringMode = false
) {
  // B3a kill switch: when the flag is off, ignore scoringMode → always build the full
  // model (old behaviour), so a parity test can compare on/off.
  scoringMode = scoringMode && config.ENABLE_SCORING_RENDER_MODEL !== false;
  const nodes = [];
  const nodesById = new Map();
  const unionNodes = new Map();

  // Decoupled SECONDARY containment bridges (set by detectCrossFamilyContainmentV3 on the
  // contour path; empty otherwise). Their marriage line is STRETCHED across the chart, so
  // it is routed on a lane BELOW the couple's row rather than drawn straight over the
  // intervening nodes — and the couple's union node sits on that lane so the shared child
  // drops cleanly from the bar. See spousePath's laneY route + the lane decomposition in
  // lineIntersection.js.
  const containmentBridges = pedigreeFamily.containmentBridgesV3 || new Set();
  // Widened both-ancestried couples (bidirectional contour): their marriage line spans the
  // tower-clearing gap. Unlike a containment bridge it is lane-routed ONLY when a node
  // actually sits between the partners on their row (see `needsLane` below). With a clean
  // gap the bar is drawn straight at row level — a lane there reads as a DOUBLE dog-leg (the
  // union dips below the row and the child drops from the dip), which is worse than the
  // wide-but-straight bar it replaces. When it IS blocked, it is excluded from the marriage-
  // length ratchet the same way as a bridge (the exclusion keys on the barBlocked marker,
  // which is stamped in both ENABLE_MARRIAGE_LANE_ROUTING states).
  const widenedCouples = pedigreeFamily.widenedCouplesV3 || new Set();
  const bridgeKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);
  const isContainmentBridge = (a, b) =>
    containmentBridges.size > 0 && containmentBridges.has(bridgeKey(a, b));
  const isWidenedCouple = (a, b) =>
    widenedCouples.size > 0 && widenedCouples.has(bridgeKey(a, b));
  // The lane a blocked marriage bar dips into, as a fraction of the gap BELOW
  // that row. Per-generation spacing makes the gap a property of the row rather
  // than of the chart (TGCLOUD-1610); with uniform rows this is the old
  // constant for every generation.
  const stretchLaneFor = (gen) =>
    (config.STRETCH_MARRIAGE_LANE ?? 0.35) * pedigreeFamily.rowGapBelow(gen);

  // const align = (pos) =>
  //   Math.round(pos / (config.GRID_SIZE * 2)) * config.GRID_SIZE * 2;

  // D2: pins persist in GRID units (gx/gy = multiples of NODE_GAP/LEVEL_HEIGHT at
  // placement time) so a spacing / auto-spacing / symbol-size change carries the
  // pinned node WITH the reflowed family. Legacy pins (raw x/y only) resolve as
  // absolute pixels until their next write upgrades them (Canvas one-shot upgrade).
  // Each axis resolves INDEPENDENTLY and only to a FINITE number — a damaged pin
  // (e.g. an arrow-nudge on a grid-only pin used to produce {x: NaN, gx: deleted})
  // must fall back to the laid-out coordinate, never bake NaN into node geometry:
  // one NaN endpoint makes SVG silently drop the whole subpath — the G-1-01391
  // "node 40 with no marriage bar" / detached-segment screenshots.
  const finite = (v) => (Number.isFinite(v) ? v : undefined);
  const pinX = (pos) =>
    pos == null
      ? undefined
      : finite(pos.gx != null ? pos.gx * config.NODE_GAP : pos.x);
  // gy is in ROW UNITS, resolved against the laid-out rows so an integer lands
  // on its generation whatever the gaps around it. With uniform rows (or no
  // per-generation table) yFromRowUnits is exactly gy * LEVEL_HEIGHT — see
  // rowUnits.js.
  const pinRows = pedigreeFamily.rowsForPins();
  const pinY = (pos) =>
    pos == null
      ? undefined
      : finite(pos.gy != null ? yFromRowUnits(pos.gy, pinRows) : pos.y);

  // Add person nodes
  pedigreeFamily.persons.forEach((person) => {
    const customPos = customPositions?.get(person.uid);
    const node = {
      pid: person.pid,
      uid: person.uid,
      name: person.name,
      gender: person.gender,
      kind: config.NODE_TYPE.NODE,
      x: pinX(customPos) ?? person.x,
      y: pinY(customPos) ?? person.y,
      virtualX: person.virtualX,
      virtualY: person.virtualY,
      rowDepth: person.rowDepth,
      // B3a: the clinical data spread is render-only; the crossing scorer never reads it.
      ...(scoringMode ? undefined : person.data),
      motherId: person.motherId,
      fatherId: person.fatherId,
      spouseIds: person.spouseIds,
      childrenIds: person.childrenIds
    };

    nodes.push(node);
    nodesById.set(person.pid, node);
  });

  // Offset 3rd, 4th, … spouses upward at increasing angles.
  // The 1st spouse is on one side, 2nd on the other side (same row).
  // The 3rd+ spouses are shifted upward so the connecting line appears
  // angled, with each successive extra spouse angled more steeply.
  //
  // Un-flanked 2-spouse hubs (ENABLE_UNFLANKED_SPOUSE_LIFT): a hub pinned at a row edge seats
  // BOTH spouses on one side, so the 2nd's bar would lane-route over the 1st. When the flag is on
  // it is treated here too, but ONLY the spouses the crossing-gated decision in
  // refineCrossingsAwareV3 approved (pedigreeFamily.liftedExtraSpouse2) are lifted — a genuinely
  // flanked 2-spouse hub has one spouse per side so offsetSide is a no-op regardless. Flag off ⇒
  // the < 3 early-return stands ⇒ byte-identical.
  // The lift is UPWARD, into the gap above the hub's row (TGCLOUD-1610: that is
  // the local gap once generations can differ in height).
  const extraSpouseYStepFor = (gen) => 0.1 * pedigreeFamily.rowGapAbove(gen);
  const processedForExtraSpouse = new Set();
  const extraSpouseInfo = new Map();
  const liftedTwoSpouse = pedigreeFamily.liftedExtraSpouse2;

  pedigreeFamily.persons.forEach((person) => {
    const nSpouses = person.spouseIds.length;
    const allowTwoSpouse =
      config.ENABLE_UNFLANKED_SPOUSE_LIFT && nSpouses === 2;
    if (nSpouses < 3 && !allowTwoSpouse) return;
    if (processedForExtraSpouse.has(person.pid)) return;
    processedForExtraSpouse.add(person.pid);

    // Mark all spouses so we don't double-process from the spouse side
    person.spouseIds.forEach((sid) => processedForExtraSpouse.add(sid));

    const personNode = nodesById.get(person.pid);
    if (!personNode) return;

    // Sort spouses by their x relative to the person (left then right)
    const spouseNodes = person.spouseIds
      .map((sid) => nodesById.get(sid))
      .filter(Boolean);

    // Separate into left and right spouses
    const leftSpouses = spouseNodes
      .filter((s) => s.x < personNode.x)
      .sort((a, b) => b.x - a.x); // closest first
    const rightSpouses = spouseNodes
      .filter((s) => s.x >= personNode.x)
      .sort((a, b) => a.x - b.x); // closest first

    // The first spouse on each side (index 0) stays at the same row.
    const offsetSide = (sideSpouses) => {
      for (let i = 1; i < sideSpouses.length; i++) {
        const sp = sideSpouses[i];
        // A 2-spouse hub's lift is crossing-gated: only angle spouses the refine approved. A 3+
        // spouse hub angles the 3rd+ unconditionally (the historical behaviour).
        if (nSpouses === 2 && !(liftedTwoSpouse && liftedTwoSpouse.has(sp.pid)))
          continue;
        const hasCustom = customPositions?.has(sp.uid);
        if (!hasCustom) {
          sp.y -= extraSpouseYStepFor(person.virtualY) * i;
        }

        extraSpouseInfo.set(sp.pid, { previousSpouseNode: sideSpouses[i - 1] });
      }
    };

    offsetSide(leftSpouses);
    offsetSide(rightSpouses);
  });

  // Lift a displaced cross-married sibship's connector OVER THE TOP (a lane above the
  // parents' row). Runs AFTER node y is settled but BEFORE edges are built. Strictly
  // gated — see displacedSibshipLift.js. SKIPPED under the contour x-assignment: its
  // tighter spacing places the displaced sibship without the over-top route, so the
  // connector runs normally (under the row) and crossings are no worse — the over-top
  // lane would only add a crossing (G-1-01373: 2 without it vs 3 with).
  if (!config.ENABLE_CONTOUR_MERGE_XASSIGN) {
    applyDisplacedSibshipLift(pedigreeFamily, nodesById);
  }

  // Dual-parent adoption (Bennett Fig 3), ENABLE_ADOPTION_DUAL_PARENT: an adopted-in person's
  // BIOLOGICAL parents are a reference couple, not a structural descent — Option A keeps the
  // child under its ADOPTIVE parents, so a BARE bio couple (added as floating "unknown relatives",
  // no parents and no children) is a disconnected component the solver has nothing to anchor: it
  // lands wherever the straggler placement puts it (the "floating top-right" the tester saw). For
  // that case ONLY, park the unpinned pair one generation above the child, to the RIGHT of the
  // child's adoptive parents, in the first COLLISION-FREE slot, so the solid connector reads
  // generationally.
  //
  // CRITICAL (G-1-01474 regression, 2026-07-27): the pass must STAND DOWN as soon as a bio parent
  // has a family of its own. When 9/10 gained their own parents (19-20, 21-22) the solver placed
  // the cluster perfectly — it even WIDENS the 9-10 couple so both parent couples sit directly
  // above with straight drops (0 dog-leg, 0 crossings) — and this parking then dragged them out of
  // those positions, collapsing the couple and creating 1.5u/4u doglegs plus 2 crossings. Parking
  // is a fallback for UNANCHORED floaters, never a competitor to the solver: once there is real
  // structure, the solver's placement is strictly better informed. (It also runs at render time,
  // downstream of refineCrossingsAwareV3, so no refine pass can undo its damage.) Pinned to the
  // real family in adoptionBioParentPlacement.test.js.
  //
  // The connector is a first-class 'bio-connector' edge (emitted below), so it follows drags + is
  // hoverable. Positions are final here (extra-spouse offset + displaced-sibship lift have run)
  // and edges are built BELOW, so the union bar and the connector both pick up the parked
  // coordinates. Runs once per real build (skipped in scoringMode so the crossing refiner is never
  // perturbed) and only when a link exists → a chart with no bio links is byte-identical.
  if (!scoringMode && config.ENABLE_ADOPTION_DUAL_PARENT) {
    const GAP = config.NODE_GAP;
    const ROW_EPS = 1;
    // "Bare" = the solver has nothing to place this person against: no drawn parents and no
    // children. A spouse link to the other bio parent doesn't count — that pair IS the floater.
    const isBare = (p) =>
      p.fatherId == null &&
      p.motherId == null &&
      (p.childrenIds || []).length === 0;
    for (const child of nodes) {
      if (child.bioFatherId == null && child.bioMotherId == null) continue;
      const adoptive = [
        child.fatherId != null ? nodesById.get(child.fatherId) : null,
        child.motherId != null ? nodesById.get(child.motherId) : null
      ].filter((a) => a && Number.isFinite(a.x) && Number.isFinite(a.y));
      if (adoptive.length === 0) continue; // no adoptive anchor → leave the couple be
      const rowY = Math.min(...adoptive.map((a) => a.y));
      const rightX = Math.max(...adoptive.map((a) => a.x));
      // The bio parents we're allowed to move: BARE ones only (see above); a dragged/pinned one
      // keeps its spot; and a bio parent that is ALSO one of the child's adoptive parents is
      // structural — never move it.
      const bioParents = [
        child.bioFatherId != null ? nodesById.get(child.bioFatherId) : null,
        child.bioMotherId != null ? nodesById.get(child.bioMotherId) : null
      ].filter(
        (p) =>
          p &&
          isBare(p) &&
          !customPositions?.has(p.uid) &&
          p.pid !== child.fatherId &&
          p.pid !== child.motherId
      );
      if (bioParents.length === 0) continue;
      const bioPids = new Set(bioParents.map((p) => p.pid));
      const coupleSpan = GAP * (bioParents.length - 1); // 0 for one, GAP for a pair

      // Occupied x on the adoptive row, right of the adoptive parents (exclude the bio parents we
      // are placing + invisible union nodes). Walk them low→high, bumping the start slot past any
      // node whose footprint would clash — leaving the couple in the first clear gap.
      const rowXs = nodes
        .filter(
          (n) =>
            n &&
            !n.hidden &&
            n.kind !== config.NODE_TYPE.UNION &&
            !bioPids.has(n.pid) &&
            Number.isFinite(n.x) &&
            Number.isFinite(n.y) &&
            Math.abs(n.y - rowY) <= ROW_EPS &&
            n.x >= rightX
        )
        .map((n) => n.x)
        .sort((a, b) => a - b);
      const startX = firstClearSlot(rowXs, rightX + GAP, coupleSpan, GAP);

      // Father then mother, left→right one GAP apart so their marriage bar draws clean.
      bioParents.forEach((p, i) => {
        p.x = startX + GAP * i;
        p.y = rowY;
      });
    }
  }

  // Decide whether a couple's marriage bar must dip onto a below-row lane. Person node
  // positions are final here (executeLayoutV2 + extra-spouse offset + displaced-sibship lift
  // have all run). Containment bridges always lane (they span intervening nodes). A widened
  // both-ancestried couple lanes ONLY when a node sits strictly between the partners on their
  // row — otherwise the straight bar at row level is clean and far more legible (the reported
  // G-1-01398 double dog-leg). A wide straight bar that would instead cross a drop-line is
  // caught by the crossing ratchet on the corpus.
  const personRowNodes = nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  const ROW_EPS = 1;
  // Partners share a row (within float noise). A lane route only makes sense for a
  // SAME-ROW bar: it dips below the shared row and comes back up. When a partner
  // sits OFF-ROW — a pinned nudge, a drag — the lane renders as a floating
  // horizontal below BOTH partners with mismatched stubs (the G-1-01391
  // "bar broken into detached segments" screenshots), so an off-row pair always
  // degrades gracefully to the straight partner-to-partner line instead (the
  // same line a cross-generational diagonal marriage draws).
  const sameRow = (a, b) => Math.abs(a.y - b.y) <= ROW_EPS;
  // Row index for the between-partners scans below. The naive form —
  // `personRowNodes.some(...)` per couple — was the single hottest line of the whole
  // scoring path (needsLane full-scans measured at ~72% of the crossing-refine at 428
  // people: ~350 couple queries × N nodes × ~900 scoring rebuilds). Group nodes by their
  // EXACT y into x-sorted rows once per build; a query then binary-searches the handful of
  // row keys within ±ROW_EPS of the couple's y and scans only the nodes inside the x gap.
  // The candidate superset is exact (any node with |n.y − a.y| ≤ EPS has its exact y within
  // the closed window) and the predicate below is applied verbatim, so the boolean is
  // byte-identical to the full scan.
  const rowsByY = new Map(); // exact y → nodes sorted by x
  for (const n of personRowNodes) {
    let arr = rowsByY.get(n.y);
    if (!arr) {
      arr = [];
      rowsByY.set(n.y, arr);
    }
    arr.push(n);
  }
  for (const arr of rowsByY.values()) arr.sort((p, q) => p.x - q.x);
  const rowKeys = [...rowsByY.keys()].sort((p, q) => p - q);
  const lowerBound = (arr, v, key) => {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((key ? key(arr[mid]) : arr[mid]) < v) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  const gapHasNodeBetween = (aPid, bPid) => {
    const a = nodesById.get(aPid);
    const b = nodesById.get(bPid);
    if (!a || !b) return false;
    if (!sameRow(a, b)) return false; // off-row (pinned) pair → straight line, never a lane
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    for (
      let ki = lowerBound(rowKeys, a.y - ROW_EPS);
      ki < rowKeys.length && rowKeys[ki] <= a.y + ROW_EPS;
      ki++
    ) {
      const row = rowsByY.get(rowKeys[ki]);
      for (
        let i = lowerBound(row, lo + ROW_EPS, (n) => n.x);
        i < row.length && row[i].x < hi - ROW_EPS;
        i++
      ) {
        const n = row[i];
        if (
          n.pid !== aPid &&
          n.pid !== bPid &&
          Math.abs(n.y - a.y) <= ROW_EPS &&
          n.x > lo + ROW_EPS &&
          n.x < hi - ROW_EPS
        )
          return true;
      }
    }
    return false;
  };
  // ANY clean same-row bar with a node strictly between the partners lanes too —
  // not just widened couples. A plain far-apart couple (e.g. C1458's 12-47 after
  // the D4 tower alignment put them on one row with the hub's sibships between)
  // otherwise draws its bar straight THROUGH those nodes (the marriage-over-node
  // failure). Cross-row diagonals are NOT affected (same-row check below); and on
  // every family whose bars are already clean this is a strict no-op — the corpus
  // marriage-overlap invariant held everywhere else, so nothing else lanes.
  const sameRowBlocked = (aPid, bPid) => {
    const a = nodesById.get(aPid);
    const b = nodesById.get(bPid);
    if (!a || !b) return false;
    if (!sameRow(a, b)) return false;
    return gapHasNodeBetween(aPid, bPid);
  };
  // Every lane form requires a genuinely same-row pair — including containment
  // bridges and widened couples: a pin that nudges one partner off-row must
  // degrade the bar to a straight line, not leave lane fragments.
  const bothOnRow = (aPid, bPid) => {
    const a = nodesById.get(aPid);
    const b = nodesById.get(bPid);
    return !!(a && b && sameRow(a, b));
  };
  const needsLane = (aPid, bPid) =>
    bothOnRow(aPid, bPid) &&
    (isContainmentBridge(aPid, bPid) ||
      (isWidenedCouple(aPid, bPid) && gapHasNodeBetween(aPid, bPid)) ||
      sameRowBlocked(aPid, bPid));
  // TGCLOUD-1577 (Kathryn, verbatim: a spouse line "coming from the bottom of
  // the icon … should never be allowed"; "must ensure spouse relationship is
  // always [middle-to-middle], even if have to space it out"; a straight bar
  // MAY "initially intersect the people"). When this flag is false, blocked
  // bars are drawn straight at row level instead of dipping onto the lane —
  // the PREDICATE above still runs and stamps `barBlocked` on the edge, so
  // every scoring consumer (spouseStretchScore / modelScore / destraddleScore
  // / the de-straddle reseed) keeps the exact same semantics in both flag
  // states.
  const laneRoutingOn = config.ENABLE_MARRIAGE_LANE_ROUTING !== false;

  // Create union nodes for couples with children
  pedigreeFamily.persons.forEach((person) => {
    if (person.spouses.length === 0 || person.children.length === 0) return;

    person.spouses.forEach((spouse) => {
      // Get children with this spouse
      const sharedChildren = person.children.filter((child) => {
        return child.motherId === spouse.pid || child.fatherId === spouse.pid;
      });

      if (sharedChildren.length === 0) return;

      // Create union node pid
      const unionId = createUnionId(person.pid, spouse.pid);

      if (unionNodes.has(unionId)) return;

      // Calculate union position (between parents, above children)
      const personPos = customPositions?.get(person.uid);
      const spousePos = customPositions?.get(spouse.uid);

      const personNode = nodesById.get(person.pid);
      const spouseNode = nodesById.get(spouse.pid);

      let px = pinX(personPos) ?? personNode?.x ?? person.x;
      let py = pinY(personPos) ?? personNode?.y ?? person.y;

      let sx = pinX(spousePos) ?? spouseNode?.x ?? spouse.x;
      let sy = pinY(spousePos) ?? spouseNode?.y ?? spouse.y;

      const hasCustomPos = !!(personPos || spousePos);

      const extraSpouseEntry = extraSpouseInfo.get(spouse.pid);

      let unionX, unionY;

      // ASYM-anchored unions take precedence over the extra-spouse step anchor
      // (Tim's ruling: "the asymmetry we accepted in the spacing between parents
      // to preference straight drops applies to multiple spouse scenarios too" —
      // the HUB-ASYM ANCHOR pass in crossingRefine marks extra-spouse unions
      // too). The park pass never marks an extra-spouse union, so unmarked
      // families render exactly as before.
      const isAsymJunction = !!pedigreeFamily.asymJunctionsV3?.has(
        person.pid < spouse.pid
          ? `${person.pid}:${spouse.pid}`
          : `${spouse.pid}:${person.pid}`
      );

      if (extraSpouseEntry && !isAsymJunction) {
        const { previousSpouseNode } = extraSpouseEntry;
        const step = config.EXTRA_SPOUSE_CHILD_X_STEP ?? 500;

        const dir = sx > px ? 1 : -1;
        const targetX = previousSpouseNode.x + dir * step;

        const lineT = sx !== px ? (targetX - px) / (sx - px) : 0.5;
        const tClamped = Math.max(0.05, Math.min(0.95, lineT));

        const rawX = px + tClamped * (sx - px);
        const rawY = py + tClamped * (sy - py);

        unionX = rawX;
        unionY = rawY;
      } else if (isAsymJunction) {
        // ASYM-anchored union (crossingRefine's park pass, HUB-ASYM ANCHOR or
        // FOUNDER-COUPLE OVER-CHILD fallback): the junction anchors over the
        // CHILDREN's midpoint and only the far bar segment stretches (desktop
        // convention — the bar need not be symmetric). Clamped to an INTERIOR
        // band of the bar (was the full [0,1]): a junction AT a partner's
        // column reads clinically as a single-parent drop (Tim's rule 2 —
        // SS01/G-1-01430, where the locked add of 10's parents pushed u(2,3)'s
        // children-midpoint past 3 and t=1 parked the drop dead under her).
        // The band keeps ≥ ASYM_JUNCTION_PARENT_CLEARANCE symbol-widths of px
        // from each partner, or the bar midpoint when the bar is too short.
        // Mirrors crossingRefine's unionVX exactly (metric ↔ render).
        // NOTE (G-8-00018): reach governance lives at the WRITERS (the
        // ASYM_JUNCTION_MAX_RESIDUAL gate in crossingRefine's HUB-ASYM /
        // IN-LAW passes) — a union only carries the asym mark when its
        // band-clamped junction lands near the children, so this branch
        // never needs (and must not grow) its own reach limit.
        const xs = sharedChildren.map((c) => nodesById.get(c.pid)?.x ?? c.x);
        const targetX = (Math.min(...xs) + Math.max(...xs)) / 2;
        const lineT = sx !== px ? (targetX - px) / (sx - px) : 0.5;
        const minOff =
          (config.ASYM_JUNCTION_PARENT_CLEARANCE ?? 0.75) *
          (config.SYMBOL_SIZE || 155);
        const span = Math.abs(sx - px);
        const tBand = span > 2 * minOff ? minOff / span : 0.5;
        const tClamped = Math.max(tBand, Math.min(1 - tBand, lineT));
        unionX = px + tClamped * (sx - px);
        unionY = py + tClamped * (sy - py);
      } else {
        const t = 0.5;
        unionX = sx + t * (px - sx);
        unionY = sy + t * (py - sy);
      }

      // A lane-routed union (containment bridge, or a widened couple whose gap is NOT clean)
      // sits on its marriage lane below the row, so the shared child drops straight from the
      // bar rather than from mid-air. A widened couple with a clean gap keeps the row-level
      // midpoint above (straight bar, no dip).
      // TGCLOUD-1577 (Kathryn): lane routing is flag-gated — with
      // ENABLE_MARRIAGE_LANE_ROUTING false the union stays at row level and
      // the bar draws straight middle-to-middle (see the spouse-edge emission
      // below for the ruling). The union then carries scoreLaneY — the lane y
      // it WOULD have sat on — so the crossing detector decomposes its child
      // drops from the legacy geometry (scoring parity: settled positions are
      // byte-identical in both flag states; only the drawing changes).
      let unionScoreLaneY = null;
      if (needsLane(person.pid, spouse.pid)) {
        const lane = stretchLaneFor(person.virtualY);
        if (laneRoutingOn) {
          unionY = Math.max(py, sy) + lane;
        } else {
          unionScoreLaneY = Math.max(py, sy) + lane;
        }
      }

      const rel = person.data?.spouseRelationships?.[spouse.pid] || {};
      const unionNode = {
        pid: unionId,
        kind: config.NODE_TYPE.UNION,
        x: unionX,
        y: unionY,
        parent1Id: person.pid,
        parent2Id: spouse.pid,
        childrenIds: sharedChildren.map((c) => c.pid),
        parentRelationship: rel.parentRelationship ?? 'married',
        isConsanguineous: rel.isConsanguineous ?? false,
        // Marks a band-clamped asym junction so unionAnchors' drag-refresh
        // replay re-clamps it against the partners' CURRENT positions
        // (ENABLE_UNION_REPLAY_CLAMP) — staircase and midpoint unions replay
        // raw, exactly as before.
        ...(isAsymJunction ? { asymBand: true } : {}),
        // TGCLOUD-1577 scoring parity (see the lane gate above): the y the
        // union would have sat on under lane routing — the detector scores
        // this union's drops from it; the drawn geometry stays at row level.
        ...(unionScoreLaneY != null ? { scoreLaneY: unionScoreLaneY } : {}),
        gender: 'union' // For rendering
      };

      nodes.push(unionNode);
      nodesById.set(unionId, unionNode);
      unionNodes.set(unionId, unionNode);
    });
  });

  const edges = [];

  // Generate spouse edges (horizontal lines between partners)
  pedigreeFamily.persons.forEach((person) => {
    person.spouses.forEach((spouse) => {
      // Only create edge once (from person with lower pid)
      if (person.pid > spouse.pid) return;

      const fromNode = nodesById.get(person.pid);
      const toNode = nodesById.get(spouse.pid);

      if (!fromNode || !toNode) return;

      const spouseRel =
        person.data?.spouseRelationships?.[spouse.pid] ||
        spouse.data?.spouseRelationships?.[person.pid] ||
        {};
      const parentRelationship = spouseRel.parentRelationship ?? 'married';
      const isConsanguineous = spouseRel.isConsanguineous ?? false;

      // Blocked marriage bar (containment bridge, widened couple with a node in
      // the gap, or any same-row bar with a node between the partners). The
      // marker is stamped in BOTH flag states — it is the scoring/reseed key —
      // while the below-row lane route itself is TGCLOUD-1577-gated: with
      // ENABLE_MARRIAGE_LANE_ROUTING false the bar draws straight
      // middle-to-middle at row level (Kathryn's ruling; it may pass through
      // intervening symbols, which the solver already penalises via
      // marriageOverCount).
      const blocked = needsLane(person.pid, spouse.pid);
      const laneValue = blocked
        ? Math.max(fromNode.y, toNode.y) + stretchLaneFor(person.virtualY)
        : null;
      const laneY = laneRoutingOn ? laneValue : null;

      const edge = {
        kind: 'spouse',
        from: person.pid,
        to: spouse.pid,
        parentRelationship,
        isConsanguineous,
        ...(blocked ? { barBlocked: true } : {}),
        ...(laneY != null ? { laneY } : {}),
        // TGCLOUD-1577 scoring parity: when the lane is NOT drawn, the blocked
        // bar still carries the lane value it WOULD have had — the crossing
        // detector decomposes it on that legacy geometry (scoreLaneY), so
        // every crossing count, Pareto gate and settled position is
        // byte-identical to the lane-routing world. Only the drawing (`d`,
        // union y, live-drag redraw via laneY) changes. Kathryn's ruling
        // licenses the straight bar's own intersections; predictability is
        // the requirement this parity preserves.
        ...(laneY == null && laneValue != null
          ? { scoreLaneY: laneValue }
          : {}),
        // A containment bridge's blocked bar is INTENTIONAL (a decoupled
        // secondary cross-marriage) — mark it so the de-straddle reseed below
        // never treats it as a defect. Keyed on `blocked`, not laneY, so the
        // exemption is identical in both flag states.
        ...(blocked && isContainmentBridge(person.pid, spouse.pid)
          ? { laneBridge: true }
          : {}),
        // B3a: skip the SVG path string when only scoring crossings (decompose rebuilds
        // the geometry from node coords + laneY; the `d` string is never read).
        d: scoringMode
          ? undefined
          : spousePath(
              { x: fromNode.x, y: fromNode.y },
              { x: toNode.x, y: toNode.y },
              parentRelationship,
              isConsanguineous,
              laneY
            )
      };

      edges.push(edge);
    });
  });

  const processedTwins = new Set();

  // child edges
  pedigreeFamily.persons.forEach((person) => {
    if (!person.motherId && !person.fatherId) return;

    if (processedTwins.has(person.pid)) return;

    const mother = person.mother;
    const father = person.father;

    if (!mother && !father) return;

    let fromId;
    let fromNode;

    // If both parents exist =  union node
    if (mother && father) {
      const unionId = createUnionId(mother.pid, father.pid);
      fromNode = nodesById.get(unionId);
      fromId = unionId;
    }
    // Single parent
    else {
      const parent = mother || father;
      fromNode = nodesById.get(parent.pid);
      fromId = parent.pid;
    }

    if (!fromNode) return;

    // Check if person is part of a twin group
    const twins = pedigreeFamily.getTwinsInSameSubGroup(person);

    if (twins.length > 1) {
      // Create single edge for entire twin group with diagonal connectors
      const twinNodes = twins.map((twin) => {
        const node = nodesById.get(twin.pid);
        return {
          ...node,
          siblingType: twin.siblingType
        };
      });

      // Filter out any null nodes
      const validTwinNodes = twinNodes.filter((n) => n && n.x !== undefined);

      if (validTwinNodes.length > 0) {
        // THE WHOLE SIBSHIP, for the glyph adjacency test (TGCLOUD-1692). A monozygotic
        // bar may only span members with nobody drawn between them, and an ordinary full
        // sibling seated between two twins is not in `twinNodes` — so without this the
        // bar would still be drawn across them. Published on the edge as well, because
        // Canvas's live-drag refresh rebuilds the path from the edge alone.
        const cohortPids = pedigreeFamily
          .getSibshipOf(person)
          .map((p) => p.pid)
          .filter((pid) => nodesById.has(pid));
        const cohortNodes = cohortPids
          .map((pid) => nodesById.get(pid))
          .filter((n) => n && n.x !== undefined);
        // `twinIds` is the POSITIONED members only, matching what the path is built
        // from. It used to publish every member of the sub-group while the path was
        // built from the filtered set, so Canvas's apex (which maps twinIds) could
        // disagree with the drawn line — harmless while the apex ignored the count,
        // wrong the moment it does not.
        const positionedTwinIds = validTwinNodes.map((n) => n.pid);
        // B3a: a twin-group edge produces NO segments in decompose (it handles only
        // 'spouse'/'child'), so it never affects the crossing score — skip its path
        // (and unknownMarkers) build entirely when scoring.
        const edge = scoringMode
          ? {
              kind: 'twin-group',
              from: fromId,
              twinIds: positionedTwinIds,
              cohortPids
            }
          : (() => {
              const { path, unknownMarkers } = twinGroupPath(
                { x: fromNode.x, y: fromNode.y },
                validTwinNodes,
                cohortNodes
              );
              return {
                kind: 'twin-group',
                from: fromId,
                twinIds: positionedTwinIds,
                cohortPids,
                d: path,
                unknownMarkers
              };
            })();

        edges.push(edge);

        // Mark all twins as processed
        twins.forEach((twin) => processedTwins.add(twin.pid));
      }
    } else {
      const toNode = nodesById.get(person.pid);

      if (!toNode) return;

      // A displaced consanguineous sibship's connector is routed OVER THE TOP (the child
      // stays in line; the sibling-line runs above the union row) — see
      // displacedSibshipLift.js. laneY is null for every ordinary child edge.
      const laneY = pedigreeFamily.overtopLaneByChild?.get(person.pid) ?? null;

      const edge = {
        kind: 'child',
        from: fromId,
        to: person.pid,
        ...(laneY != null ? { laneY } : {}),
        // B3a: skip the SVG path string when only scoring crossings.
        d: scoringMode
          ? undefined
          : childPath(
              { x: fromNode.x, y: fromNode.y },
              { x: toNode.x, y: toNode.y },
              laneY
            )
      };

      edges.push(edge);
    }
  });

  // === Reproductive satellites (Track A / A3) ===
  // Gamete donors (Bennett Fig 5 "D") are placed BESIDE the carrier with a solid line of
  // descent to each offspring and NO mating line — deliberately kept OUT of the contour
  // engine (a donor carries donorOfPids, never childrenIds, so it never becomes a couple/
  // union). This post-pass positions each donor on the carrier's row and emits a
  // `repro-descent` edge per offspring. Gated on ENABLE_REPRO_NODES and inert unless a
  // donor node is present, so existing families are byte-identical. In scoringMode the
  // person.data spread is skipped, so no node carries reproKind and this whole pass no-ops
  // (harmless — repro-descent edges are not counted by the crossing detector anyway).
  // NOTE: surrogates (which ARE the carrier, not a satellite) are handled with their own
  // fixtures once the multi-satellite wiring is GC-signed-off (PLAN §4.4/§20).
  // TGCLOUD-1597 — the pass is now in two halves, because an IDENTIFIED donor is a real member.
  // Selection widens from "carries reproKind: gameteDonor" to "carries a non-empty donorOfPids",
  // so a promoted member emits the SAME `repro-descent` diagonal with the same sibship-bar and
  // twin-apex aiming (the notation must not depend on whether the donor has a name). But only a
  // SATELLITE is repositioned: a real member keeps whatever seat the layout gave them, per 1597 —
  // "don't try and move anyone automatically". Their line simply crosses the pedigree.
  if (config.ENABLE_REPRO_NODES) {
    const donorNodes = nodes.filter(
      (n) =>
        n.kind === config.NODE_TYPE.NODE &&
        (isGameteDonorNode(n) ||
          (config.ENABLE_DONOR_IDENTITY &&
            Array.isArray(n.donorOfPids) &&
            n.donorOfPids.length > 0))
    );
    // Satellites this pass has already seated — see the occupancy note below.
    const placedSatellitePids = new Set();
    for (const donor of donorNodes) {
      const offspringNodes = (
        Array.isArray(donor.donorOfPids) ? donor.donorOfPids : []
      )
        .map((pid) => nodesById.get(pid))
        .filter(Boolean);
      if (offspringNodes.length === 0) continue;

      // The carrier is an offspring's in-graph parent (the mother/father that is set).
      const firstOff = offspringNodes[0];
      const carrier =
        nodesById.get(firstOff.motherId) ||
        nodesById.get(firstOff.fatherId) ||
        null;

      // Satellites only. A promoted donor is a real person with their own place in the graph (and
      // possibly their own relatives hanging off them) — moving them beside the carrier would drag
      // their whole branch across the chart.
      //
      // ANCHOR (Tim's QA on G-1-01491): this used to require a `carrier`, i.e. an in-graph PARENT of
      // the offspring. Where the offspring has no parents, a satellite therefore got no placement at
      // all — and being a derived node with no childrenIds and no spouseIds, the contour engine seats
      // it as a free-floating DISCONNECTED COMPONENT. Its position was then an optimiser artefact, so
      // Auto-arrange (which clears the established order and re-packs components) could fling it to
      // the far side of an unrelated subtree, with its descent line crossing everything in between.
      // On 01491 node 4's two donors landed either side of nodes 10 and 11.
      //
      // So the anchor now falls back to the OFFSPRING: a donor is a genetic parent, so one generation
      // above the child it produced is the right place for it when there is no carrier to sit beside.
      // That makes the position DETERMINISTIC — derived from the graph, never from the packing — which
      // is what stops Auto-arrange from being able to make it worse.
      const anchor = carrier || firstOff;
      // A genetic parent's generation — the row ABOVE the offspring, which under
      // per-generation spacing is its own gap up, not the global pitch.
      const anchorRowY = carrier
        ? carrier.y
        : firstOff.y - pedigreeFamily.rowGapAbove(firstOff.virtualY);
      if (anchor && isGameteDonorNode(donor)) {
        const rowY = anchorRowY;
        // Occupancy is REAL nodes plus satellites already seated by this pass. A satellite that has
        // not been placed yet is still sitting whereever component packing dropped it and is about to
        // move, so treating it as an obstacle pushed later donors past slots that were in fact free —
        // on 01491 that alone cost node 4's donors two extra gaps.
        const rowNodes = nodes.filter(
          (n) =>
            n.kind === config.NODE_TYPE.NODE &&
            n.pid !== donor.pid &&
            Math.abs(n.y - rowY) <= 1 &&
            (!isGameteDonorNode(n) || placedSatellitePids.has(n.pid))
        );
        const clashes = (cx) =>
          rowNodes.some((n) => Math.abs(n.x - cx) < config.NODE_GAP);
        // ALWAYS the side away from the anchor's partner, so the donor's line of descent never runs
        // back across the couple's marriage bar or their children's drop.
        //
        // Taking the nearest free slot on EITHER side was tried and reverted: it shortened node 4's
        // lines on 01491 but pushed node 7's donors to the far side of node 7's own parents, so their
        // lines crossed that couple's bar and read as though the donors belonged to it. Shorter is not
        // worth ambiguous. The genuine fix for a crowded row is width reservation, not reallocation.
        const partnerLeft = (anchor.spouseIds || [])
          .map((pid) => nodesById.get(pid))
          .filter(Boolean)
          .some((s) => s.x < anchor.x);
        const side = partnerLeft ? 1 : -1; // partner on the left → donor on the right
        let x = anchor.x + side * config.NODE_GAP;
        let guard = 0;
        while (clashes(x) && guard++ < 1000) x += side * config.NODE_GAP;
        donor.x = x;
        donor.y = rowY;
        placedSatellitePids.add(donor.pid);
      }

      // Solid line(s) of descent, a straight DIAGONAL from the donor's bottom edge
      // (Bennett Fig 5), NOT a child elbow. Group the donor's offspring by shared
      // parentage: when one donor produced MULTIPLE children in the SAME sibship
      // (Bennett 5.1c — a born child + a pregnancy from the same donor), draw a SINGLE
      // line to the right-angle corner of that sibship's bar (where it already fans out
      // to every child), so it reads as "this donor produced the whole sibship" rather
      // than one crossing line per child. A lone offspring — or offspring in different
      // sibships — keeps a direct diagonal to that child's top edge.
      // Offspring are grouped by shared parentage so one donor who produced a whole sibship draws
      // ONE line to the sibship bar (Bennett 5.1c) instead of a diagonal per child.
      //
      // A node with NEITHER parent must key UNIQUELY. Two parentless people are not siblings, but
      // `${motherId}|${fatherId}` collapsed every one of them onto the same `'|'` key, so they were
      // treated as one sibship and only `group[0]` got a line. Tim's QA on G-1-01491: persons 8 and 9
      // were both recorded against the same donor and neither has parents, so the second line simply
      // never drew — and deleting one record made the other's line appear, which is what "it unblocks
      // that particular line" was.
      const sibKey = (n) =>
        n.motherId == null && n.fatherId == null
          ? `solo:${n.pid}`
          : `${n.motherId ?? ''}|${n.fatherId ?? ''}`;
      const sibGroups = new Map();
      for (const off of offspringNodes) {
        const k = sibKey(off);
        if (!sibGroups.has(k)) sibGroups.set(k, []);
        sibGroups.get(k).push(off);
      }
      for (const group of sibGroups.values()) {
        const sharedCarrier =
          group.length >= 2
            ? nodesById.get(group[0].motherId) ||
              nodesById.get(group[0].fatherId) ||
              null
            : null;
        // Aim at the sibling nearest the donor so the line takes the shortest route to
        // the bar (its corner above that child); a singleton just uses its one offspring.
        const target =
          sharedCarrier != null
            ? group.reduce((a, b) =>
                Math.abs(b.x - donor.x) < Math.abs(a.x - donor.x) ? b : a
              )
            : group[0];
        // TWINS: the offspring's drop does not come off a sibship bar at all — it forks at the twin
        // APEX partway down, so aiming at the bar corner leaves the donor line visibly short of the
        // fork (QA on a twin pregnancy with egg + sperm donors). When every offspring in this group
        // is in one twin sub-group, aim at that apex instead. The apex pids are stored on the edge
        // so the live-drag refresh recomputes the identical point (same contract as
        // sibBarCarrierPid).
        const twinSub = group[0].twinSubGroupId;
        const isTwinGroup =
          !!twinSub &&
          group.length >= 2 &&
          group.every((g) => g.twinSubGroupId === twinSub);
        const twinUnion = isTwinGroup
          ? nodesById.get(
              createUnionId(group[0].motherId, group[0].fatherId)
            ) ||
            nodesById.get(group[0].motherId) ||
            nodesById.get(group[0].fatherId) ||
            null
          : null;
        // THE WHOLE SET, not the donor's own offspring (TGCLOUD-1692). `group` is the
        // people THIS donor contributed to, which for a triplet whose donor gave to only
        // two of the three is a strict SUBSET — so the apex was computed from the wrong
        // members and the donor line aimed at a fork that is not where the diagonals
        // actually meet. Latent before now because the apex ignored the member count;
        // wrong in two ways once it does not.
        const twinSetNodes = isTwinGroup
          ? [...nodesById.values()].filter(
              (nd) =>
                nd &&
                nd.twinSubGroupId === twinSub &&
                Number.isFinite(nd.x) &&
                nd.motherId === group[0].motherId &&
                nd.fatherId === group[0].fatherId
            )
          : null;
        const apexMembers = twinSetNodes?.length ? twinSetNodes : group;
        const apex = twinUnion ? twinApex(twinUnion, apexMembers) : null;
        edges.push({
          kind: 'repro-descent',
          from: donor.pid,
          to: target.pid,
          ...(sharedCarrier != null && !apex
            ? { sibBarCarrierPid: sharedCarrier.pid }
            : {}),
          ...(apex
            ? {
                twinApexUnionPid: twinUnion.pid ?? twinUnion.id,
                // The SET's pids, so Canvas's live drag recomputes the identical apex.
                twinApexPids: apexMembers.map((g) => g.pid)
              }
            : {}),
          d: scoringMode
            ? undefined
            : reproDescentPath(donor, target, sharedCarrier, apex)
        });
      }
    }
  }

  // Dual-parent adoption (Bennett Fig 3), ENABLE_ADOPTION_DUAL_PARENT: emit the biological
  // connector as a FIRST-CLASS edge (like 'repro-descent') rather than a Canvas-only overlay, so
  // the live-drag refresh and the hit-area machinery pick it up (it now follows a dragged node and
  // is hoverable). from = child; to = one bio parent; coPid = the other (when both are linked) so
  // the path re-aims at their midpoint. The crossing detector returns [] for unknown kinds, so it
  // never perturbs hop-overs. Gated + only present when a link exists → byte-identical for every
  // existing chart (the whole golden corpus). Emitted only in the real build (scoringMode skips
  // path strings, and the detector ignores the kind anyway).
  if (!scoringMode && config.ENABLE_ADOPTION_DUAL_PARENT) {
    for (const child of nodes) {
      const bioF =
        child.bioFatherId != null ? nodesById.get(child.bioFatherId) : null;
      const bioM =
        child.bioMotherId != null ? nodesById.get(child.bioMotherId) : null;
      const primary = bioF || bioM;
      if (!primary) continue;
      edges.push({
        kind: 'bio-connector',
        from: child.pid,
        to: primary.pid,
        ...(bioF && bioM ? { coPid: bioM.pid } : {}),
        d: bioConnectorPath(child, [bioF, bioM])
      });
    }
  }

  // ——— SIBLING-LINE DE-MERGE (TGCLOUD-1578, Kathryn: "Ability to offset sibling
  // line", related to TGCLOUD-1577 "but also required in other scenarios") ———
  //
  // A sibship's horizontal is the union of its per-child elbows, all at
  // midY = (uy+cy)/2 — so every sibship between the same two generations sits on
  // ONE shared lane, and when two sibships' bar extents overlap in x they merge
  // into a single ambiguous line (which children belong to which parents?). The
  // merge is invisible to the optimiser (parallel horizontals never score), so no
  // layout pass can be pressured into fixing it — desktop resolves it by drawing
  // the colliding sibling lines at OFFSET heights, and that is what this pass
  // does: colliding bars are staggered by SIBLING_LINE_OFFSET_STEP × LEVEL_HEIGHT
  // (down first — parent labels hang into the upper half of the band).
  //
  // Score-the-fiction (the TGCLOUD-1577 precedent): the offset is DRAWN but the
  // crossing detector keeps scoring the legacy midY (edge.laneDrawnOnly — see
  // lineIntersection.js), so every crossing count, Pareto gate and settled
  // position is byte-identical with the pass on or off; crossings on the moved
  // horizontal are noArc-tagged (nothing is drawn at the scored y). Twin-bearing
  // sibships are excluded (the twin fan's apex shares the legacy lane and does
  // not follow laneY). fromNode.sibLaneY carries the drawn bar y so Canvas's
  // sibling-group hit/hover geometry stays on the ink.
  if (config.ENABLE_SIBLING_LINE_DEMERGE !== false) {
    // A fraction of the gap the bar sits IN — i.e. the gap above the children's
    // row (TGCLOUD-1610). Bars in one collision bucket share a row pair, so the
    // step stays consistent within a bucket.
    const stepFor = (childGen) =>
      (config.SIBLING_LINE_OFFSET_STEP ?? 0.1) *
      (pedigreeFamily.rowGapAbove(childGen) || 1200);
    const TOUCH = 50; // px — bars nearer than this read as one continuous line
    const twinFroms = new Set(
      edges.filter((e) => e.kind === 'twin-group').map((e) => e.from)
    );
    const bars = new Map(); // (from @ rounded drawn midY) → bar
    for (const e of edges) {
      if (e.kind !== 'child' || e.laneY != null) continue;
      if (twinFroms.has(e.from)) continue;
      const f = nodesById.get(e.from);
      const c = nodesById.get(e.to);
      if (!f || !c) continue;
      if (Math.abs(f.x - c.x) < 1) continue; // straight drop — no horizontal
      const y = (f.y + c.y) / 2; // DRAWN midY (row-level union under 1577)
      const key = `${e.from}@${Math.round(y)}`;
      let b = bars.get(key);
      if (!b) {
        b = {
          fromNode: f,
          y,
          x1: f.x,
          x2: f.x,
          edges: [],
          childGen: c.virtualY
        };
        bars.set(key, b);
      }
      b.x1 = Math.min(b.x1, c.x);
      b.x2 = Math.max(b.x2, c.x);
      b.edges.push(e);
    }
    // Stale-stamp hygiene: fromNode objects can be long-lived engine persons.
    for (const b of bars.values()) {
      if (b.fromNode.sibLaneY != null) delete b.fromNode.sibLaneY;
    }
    const byY = new Map();
    for (const b of bars.values()) {
      const yk = Math.round(b.y);
      let list = byY.get(yk);
      if (!list) byY.set(yk, (list = []));
      list.push(b);
    }
    for (const list of byY.values()) {
      if (list.length < 2) continue;
      list.sort(
        (a, b) =>
          a.x1 - b.x1 ||
          String(a.edges[0].from).localeCompare(String(b.edges[0].from))
      );
      const placed = []; // {x1, x2, ly} — bars already assigned a drawn lane
      for (const b of list) {
        // Smallest offset (0, +1, −1, +2, −2 … steps) whose lane is clear of
        // every already-placed overlapping bar. Deterministic: bars are
        // processed left-to-right, first bar always keeps the legacy lane.
        let chosen = null;
        const STEP = stepFor(b.childGen);
        for (let i = 0; i <= list.length && !chosen; i++) {
          for (const k of i === 0 ? [0] : [i, -i]) {
            const ly = b.y + k * STEP;
            const clash = placed.some(
              (p) =>
                Math.abs(p.ly - ly) < 1 &&
                b.x1 < p.x2 + TOUCH &&
                p.x1 < b.x2 + TOUCH
            );
            if (!clash) {
              chosen = { k, ly };
              break;
            }
          }
        }
        const ly = chosen ? chosen.ly : b.y;
        placed.push({ x1: b.x1, x2: b.x2, ly });
        if (!chosen || chosen.k === 0) continue;
        b.fromNode.sibLaneY = ly;
        for (const e of b.edges) {
          e.laneY = ly;
          e.laneDrawnOnly = true; // scored at legacy midY — see decompose
          if (!scoringMode) {
            const f = nodesById.get(e.from);
            const c = nodesById.get(e.to);
            e.d = childPath({ x: f.x, y: f.y }, { x: c.x, y: c.y }, ly);
          }
        }
      }
    }
  }

  const model = {
    nodes,
    edges,
    nodesById
  };
  // Only present when generations differ in height — the key's absence is what
  // keeps a uniform layout's model byte-identical to before (TGCLOUD-1610).
  if (pinRows) model.rows = pinRows;
  return model;
}

// B2: a single, self-contained layout pass — the standalone form of pedigree()'s internal
// `layoutOnce` closure. Builds a fresh PedigreeFamily, runs the V2 contour engine (with an
// optional forced ancestry mode) and the crossing refinement, and returns the laid-out
// family. It manages ENABLE_BIDIRECTIONAL_CONTOUR itself (save/restore) so callers never
// leave the shared config toggled. Used by the parallel orchestrator (layoutParallel.js) to
// run the descent-forest and the two ancestry modes as independent tasks; byte-identical to
// `layoutOnce` for the same (bidir, ancModeForce) — pinned by the parallel parity test.
export function runOneLayout({
  family,
  familyId,
  customPositions,
  locked = null,
  seedOrder = null,
  seeded = false,
  bidir = true,
  ancModeForce = null,
  tooBig = false,
  // Optional telemetry sink: when supplied, receives {v2Ms, refineMs} — the wall-clock split
  // between the engine (placement + search) and the crossing refinement. Pure observation
  // (never affects the layout); the sync path never passes it.
  _phases = null,
  // Optional observer invoked with the laid-out family BETWEEN the engine and the crossing
  // refinement — the earliest point the engine's bidirUnsupported verdict exists. The
  // parallel orchestrator's verdict-first dispatch uses it to decide whether the other
  // ancestry-mode tasks are worth starting while this task's refine still runs. Pure
  // observation (must not mutate); the sync path never passes it.
  _onEngineDone = null
}) {
  const prevBidir = config.ENABLE_BIDIRECTIONAL_CONTOUR;
  config.ENABLE_BIDIRECTIONAL_CONTOUR = bidir;
  try {
    const pf = new PedigreeFamily(familyId);
    family.forEach((personData) => pf.addPerson(personData));
    const t0 = Date.now();
    if (config.ENABLE_LAYOUT_V2)
      executeLayoutV2(
        pf,
        buildRenderModel,
        locked,
        seeded ? seedOrder : null,
        ancModeForce
      );
    else pf.executeLayout();
    const t1 = Date.now();
    if (_onEngineDone) _onEngineDone(pf);
    if (!tooBig)
      refineCrossingsAwareV3(pf, customPositions, buildRenderModel, !!locked);
    if (_phases) {
      _phases.v2Ms = t1 - t0;
      _phases.refineMs = Date.now() - t1;
    }
    return pf;
  } finally {
    config.ENABLE_BIDIRECTIONAL_CONTOUR = prevBidir;
  }
}

// A family has a BOTH-ANCESTRIED couple when both partners have a drawn parent — the only
// structure where the bidirectional contour can beat the descent-forest (it keeps such a
// couple together instead of splitting it). Cheap pre-check on the raw family so the
// best-of-both double layout below runs ONLY for families that could benefit.
export function hasBothAncestriedCouple(family) {
  const byPid = new Map(family.map((p) => [p.pid, p]));
  for (const p of family) {
    if (p.fatherId == null && p.motherId == null) continue;
    for (const sid of p.spouseIds || []) {
      const sp = byPid.get(sid);
      if (sp && (sp.fatherId != null || sp.motherId != null)) return true;
    }
  }
  return false;
}

// HOP-OVER score of a rendered model: line crossings (real detector) + marriage lines that
// run over a same-row node (the parallel/overlapping-line defect). Weighted equally; used to
// pick between the descent-forest and bidirectional contour.
export function hopoverScore(model, pedigreeFamily) {
  const map = detectAllSegmentIntersections(model.edges, model.nodesById);
  const cross = map ? map.size ?? 0 : 0;
  const persons = model.nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  const byPid = new Map(persons.map((n) => [n.pid, n]));
  const rowOf = (n) => Math.round(n.y / 10) * 10;
  const bridges = pedigreeFamily.containmentBridgesV3 || new Set();
  const key = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);
  let over = 0;
  const seen = new Set();
  for (const p of pedigreeFamily.persons) {
    for (const sp of p.spouses || []) {
      const k = key(p.pid, sp.pid);
      if (seen.has(k)) continue;
      seen.add(k);
      if (bridges.has(k)) continue;
      const a = byPid.get(p.pid);
      const b = byPid.get(sp.pid);
      if (!a || !b || rowOf(a) !== rowOf(b)) continue;
      const lo = Math.min(a.x, b.x);
      const hi = Math.max(a.x, b.x);
      if (
        persons.some(
          (n) =>
            n.pid !== a.pid &&
            n.pid !== b.pid &&
            rowOf(n) === rowOf(a) &&
            n.x > lo + 1 &&
            n.x < hi - 1
        )
      )
        over++;
    }
  }
  return { cross, over, sum: cross + over };
}

// SPOUSE-STRETCH score: marriage-bar length beyond (own-sibship span + a 2-slot
// allowance), summed over same-row, un-laned spouse edges (lane-routed bridges are
// excluded via laneY). This is the G-1-01429 blind spot: a couple torn 8.5 slots apart
// over EMPTY space ties 0 crossings / 0 marriage-over-NODE / 0 dog-leg (the union
// midpoint still sat exactly over the child), so neither the beacon nor best-of-both
// could see it. Subtracting the couple's own children's span keeps the DELIBERATE wide
// bars legal: a hub-edge-straddle stretches its marriage line across its own sibship
// (accepted trade, TGCLOUD-1456/1468) and must score zero — G-1-01391's settle broke
// when a plain length-based version of this term charged it. The tear stays chargeable
// because its sibship is a single child (span 0). Consumers additionally apply a
// 2×NODE_GAP deadband so only a SUBSTANTIAL difference ever decides anything. Exported
// for tests.
export function spouseStretchScore(model) {
  const allowance = 2 * (config.NODE_GAP || 800);
  // Couple key → their shared children's x-span, from the union nodes.
  const coupleKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);
  const kidSpan = new Map();
  for (const u of model.nodes) {
    if (u.kind !== config.NODE_TYPE.UNION) continue;
    if (u.parent1Id == null || u.parent2Id == null) continue;
    const xs = (u.childrenIds || [])
      .map((id) => model.nodesById.get(id))
      .filter(Boolean)
      .map((k) => k.x);
    if (!xs.length) continue;
    const k = coupleKey(u.parent1Id, u.parent2Id);
    const span = Math.max(...xs) - Math.min(...xs);
    kidSpan.set(k, Math.max(kidSpan.get(k) || 0, span));
  }
  let stretch = 0;
  for (const e of model.edges) {
    // Keyed on the blocked-bar MARKER (not laneY) so the exclusion set is
    // identical whether or not lane routing is drawn (TGCLOUD-1577).
    if (e.kind !== 'spouse' || e.barBlocked) continue;
    const a = model.nodesById.get(e.from);
    const b = model.nodesById.get(e.to);
    if (!a || !b || Math.abs(a.y - b.y) > 1) continue;
    const span = kidSpan.get(coupleKey(e.from, e.to)) || 0;
    stretch += Math.max(0, Math.abs(a.x - b.x) - span - allowance);
  }
  return stretch;
}

// DOG-LEG score of a rendered model: total horizontal offset (px) of each union from the
// midpoint of its children — i.e. the elbow `childPath` is forced to draw. Used ONLY as a
// tie-break in best-of-both (when crossings AND marriage-over-node are already equal), so a
// simple both-ancestried family adopts the straight-drop bidirectional layout instead of the
// descent-forest's offset one. It can never override a hop-over difference, so it cannot
// reintroduce a crossing or a parallel line (those break the tie first).
export function doglegScore(model) {
  const persons = model.nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  const byPid = new Map(persons.map((n) => [n.pid, n]));
  let sum = 0;
  for (const u of model.nodes) {
    if (u.kind !== config.NODE_TYPE.UNION) continue;
    const kids = (u.childrenIds || [])
      .map((id) => byPid.get(id))
      .filter(Boolean);
    if (!kids.length) continue;
    // MIDRANGE, not mean (2026-08-12): the optimiser (crossingRefine's
    // childMidVX), the render junction anchors and the drawn drop bus all use
    // (min+max)/2 — a mean-based selector charged a DEAD-STRAIGHT union a
    // phantom dog-leg whenever a sibship bunches (a twin block at one end of
    // G-8-00018's rail scored 1.5u on a 0-offset union), and that artifact
    // alone exceeds the whole 1-NODE_GAP decision deadband, silently steering
    // best-of-both, the D7 orientation choice and the arrange beacon. The
    // selector must measure the same quantity the optimiser optimises.
    const xs = kids.map((k) => k.x);
    const mid = (Math.min(...xs) + Math.max(...xs)) / 2;
    sum += Math.abs(u.x - mid);
  }
  return sum;
}

// Capture the settled within-generation order of a rendered layout as Map<uid, rank>
// (rank = 0-based left-to-right index within the person's generation). Persisted as a
// pedigree's establishedOrder so subsequent layouts reproduce it (Phase 2 "sit and forget").
export function captureEstablishedOrder(nodes) {
  const persons = (nodes || []).filter((n) => n.kind === config.NODE_TYPE.NODE);
  const byGen = new Map();
  for (const n of persons) {
    const y = Math.round(n.virtualY ?? 0);
    if (!byGen.has(y)) byGen.set(y, []);
    byGen.get(y).push(n);
  }
  const order = new Map();
  for (const [, arr] of byGen) {
    arr.sort((a, b) => (a.virtualX ?? 0) - (b.virtualX ?? 0));
    arr.forEach((n, i) => {
      if (n.uid != null) order.set(n.uid, i);
    });
  }
  return order;
}

// Does a rendered layout AGREE with a captured within-generation order — i.e. is
// every ranked uid in the same relative left-to-right sequence per generation? A
// newly-added (unranked) node never disagrees; only a strict inversion between two
// ranked people does. Used by the locked fast-path below.
export const rankedSequenceAgrees = (nodes, order) => {
  const byGen = new Map();
  for (const n of nodes || []) {
    if (n.kind !== config.NODE_TYPE.NODE || !order.has(n.uid)) continue;
    const g = Math.round(n.virtualY ?? 0);
    if (!byGen.has(g)) byGen.set(g, []);
    byGen.get(g).push(n);
  }
  for (const [, arr] of byGen) {
    arr.sort((a, b) => (a.virtualX ?? 0) - (b.virtualX ?? 0));
    for (let i = 1; i < arr.length; i++) {
      if (order.get(arr[i - 1].uid) > order.get(arr[i].uid)) return false;
    }
  }
  return true;
};

// Kendall-style concordance between an order map and a model's left-to-right x
// sequences: +1 = follows the order exactly, -1 = exactly mirrored. Used as the
// D7 full-tie orientation tie-break.
const orientationConcordance = (order, model) => {
  const byGen = new Map();
  for (const n of model.nodes || []) {
    if (n.kind !== config.NODE_TYPE.NODE || !order.has(n.uid)) continue;
    const g = Math.round(n.virtualY ?? 0);
    if (!byGen.has(g)) byGen.set(g, []);
    byGen.get(g).push(n);
  }
  let conc = 0;
  let disc = 0;
  for (const [, arr] of byGen) {
    arr.sort((a, b) => (a.virtualX ?? 0) - (b.virtualX ?? 0));
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        if (order.get(arr[i].uid) < order.get(arr[j].uid)) conc++;
        else disc++;
      }
    }
  }
  return (conc - disc) / (conc + disc || 1);
};

// Model-only quality score for the D7 orientation comparison (no family instance
// needed): real crossings + marriage-over-node (same-row, un-laned bars only —
// lanes and diagonals are clean by construction).
export const modelScore = (model) => {
  const map = detectAllSegmentIntersections(model.edges, model.nodesById);
  const cross = map ? map.size ?? 0 : 0;
  const persons = model.nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  let over = 0;
  for (const e of model.edges) {
    // barBlocked (not laneY): identical exclusion in both lane-flag states.
    if (e.kind !== 'spouse' || e.barBlocked) continue;
    const a = model.nodesById.get(e.from);
    const b = model.nodesById.get(e.to);
    if (!a || !b || Math.abs(a.y - b.y) > 1) continue;
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    if (
      persons.some(
        (n) =>
          n.pid !== e.from &&
          n.pid !== e.to &&
          Math.abs(n.y - a.y) <= 1 &&
          n.x > lo + 1 &&
          n.x < hi - 1
      )
    ) {
      over++;
    }
  }
  return { sum: cross + over, over, stretch: spouseStretchScore(model) };
};

// ——— DE-STRADDLE RESEED (G-1-01391 item 3, 2026-07-11) ———
// A couple whose marriage bar LANES because a whole OTHER COUPLE (and/or a free
// leaf) sits between the partners — 01391's `38 | 34 35 | 39` with 56 wedged
// inside 36-37 one row down — is an ORDER defect none of the local sweeps can
// reach: the adjacent-swap can't move a couple-block across a partner without a
// transient regression, and the leaf park alone pays a dog cost its allowance
// rejects. The clean order provably exists (forced-order probe: cross 2→0,
// over 2→0, lanes 2→0, dog 4.13→0.34). So: detect the pattern on the FINISHED
// model, build the ≤4 deterministic rank mutations (move one straddling partner
// to the far side of the block — both partners tried; park each wedged leaf on
// the side of its nearest sibling, else its parents' union), relay the family
// out LOCKED to each mutated order, and adopt one ONLY when it strictly
// improves (fewer crossings, over and lanes not worse). Deterministic and
// bounded (≤4 extra layouts, only for families that exhibit the pattern);
// containment bridges are exempt (their lane is intentional).
export const destraddleScore = (model) => {
  const map = detectAllSegmentIntersections(model.edges, model.nodesById);
  const cross = map ? map.size ?? 0 : 0;
  const persons = model.nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  let over = 0;
  let lanes = 0;
  for (const e of model.edges) {
    if (e.kind !== 'spouse' || e.laneBridge) continue;
    // `lanes` counts blocked non-bridge bars via the barBlocked marker, so
    // the score is identical whether the lane dip is drawn or not
    // (TGCLOUD-1577). Field name kept — layoutParallel.js consumes it.
    if (e.barBlocked) lanes++;
    const a = model.nodesById.get(e.from);
    const b = model.nodesById.get(e.to);
    if (!a || !b || Math.abs(a.y - b.y) > 1) continue;
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    if (
      persons.some(
        (n) =>
          n.pid !== e.from &&
          n.pid !== e.to &&
          Math.abs(n.y - a.y) <= 1 &&
          n.x > lo + 1 &&
          n.x < hi - 1
      )
    )
      over++;
  }
  return { cross, over, lanes };
};

export const destraddleCandidateOrders = (model) => {
  const persons = model.nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  const byPid = new Map(persons.map((n) => [n.pid, n]));
  // Blocked, non-bridge marriages whose in-between nodes decompose entirely
  // into free leaves and single-married couple-blocks. Keyed on barBlocked
  // (not laneY) so the reseed fires identically whether the lane dip is
  // drawn or not (TGCLOUD-1577).
  const defects = [];
  for (const e of model.edges) {
    if (e.kind !== 'spouse' || !e.barBlocked || e.laneBridge) continue;
    const a = byPid.get(e.from);
    const b = byPid.get(e.to);
    if (!a || !b || Math.abs(a.y - b.y) > 1) continue;
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    const between = persons
      .filter(
        (n) =>
          n.pid !== a.pid &&
          n.pid !== b.pid &&
          Math.abs(n.y - a.y) <= 1 &&
          n.x > lo + 1 &&
          n.x < hi - 1
      )
      .sort((x1, x2) => x1.x - x2.x);
    if (!between.length) continue;
    const pids = new Set(between.map((n) => n.pid));
    const leaves = [];
    let hasBlock = false;
    let clean = true;
    const used = new Set();
    for (const n of between) {
      if (used.has(n.pid)) continue;
      const sp = n.spouseIds || [];
      if (!(n.childrenIds || []).length && !sp.length) {
        leaves.push(n);
        used.add(n.pid);
      } else if (sp.length === 1 && pids.has(sp[0])) {
        const w = byPid.get(sp[0]);
        if (w && (w.spouseIds || []).length === 1) {
          hasBlock = true;
          used.add(n.pid);
          used.add(w.pid);
        } else {
          clean = false;
          break;
        }
      } else {
        clean = false;
        break;
      }
    }
    if (clean) defects.push({ a, b, leaves, hasBlock });
  }
  if (!defects.length) return [];

  // Base per-row uid sequences (left-to-right), keyed by rounded virtualY.
  const rows = new Map();
  for (const n of persons) {
    const g = Math.round(n.virtualY ?? 0);
    if (!rows.has(g)) rows.set(g, []);
    rows.get(g).push(n);
  }
  for (const arr of rows.values())
    arr.sort((x1, x2) => (x1.virtualX ?? 0) - (x2.virtualX ?? 0));
  const baseRows = new Map(
    [...rows.entries()].map(([g, arr]) => [g, arr.map((n) => n.uid)])
  );

  const rowOf = (n) => Math.round(n.virtualY ?? 0);
  const parkSide = (leaf, a, b) => {
    // Park a wedged leaf on the side of its nearest sibling (same parents);
    // else the side of its parents' union; else left. Deterministic.
    const sibs = persons.filter(
      (n) =>
        n.pid !== leaf.pid &&
        n.fatherId === leaf.fatherId &&
        n.motherId === leaf.motherId &&
        (leaf.fatherId != null || leaf.motherId != null)
    );
    let refX = null;
    if (sibs.length) {
      let bestD = Infinity;
      for (const s of sibs) {
        const d = Math.abs(s.x - leaf.x);
        if (d < bestD) {
          bestD = d;
          refX = s.x;
        }
      }
    } else if (leaf.fatherId != null && leaf.motherId != null) {
      const u = model.nodesById.get(
        `union-${[leaf.fatherId, leaf.motherId].sort()[0]}-${
          [leaf.fatherId, leaf.motherId].sort()[1]
        }`
      );
      if (u) refX = u.x;
    }
    const mid = (a.x + b.x) / 2;
    return refX == null ? -1 : refX < mid ? -1 : 1;
  };

  // Straddle defects get two variants (move left partner right of the block /
  // move right partner left of it); leaf parks are applied to every variant.
  const straddles = defects.filter((d) => d.hasBlock);
  const variantSets = [];
  const nVariants = Math.min(4, Math.pow(2, straddles.length) || 1);
  for (let mask = 0; mask < nVariants; mask++) variantSets.push(mask);

  const candidates = [];
  for (const mask of variantSets) {
    const rowsCopy = new Map(
      [...baseRows.entries()].map(([g, arr]) => [g, arr.slice()])
    );
    const moveAdjacent = (g, movedUid, anchorUid, side) => {
      const arr = rowsCopy.get(g);
      if (!arr) return;
      const from = arr.indexOf(movedUid);
      if (from < 0) return;
      arr.splice(from, 1);
      const at = arr.indexOf(anchorUid);
      if (at < 0) return;
      arr.splice(side < 0 ? at : at + 1, 0, movedUid);
    };
    straddles.forEach((d, i) => {
      const [left, right] = d.a.x <= d.b.x ? [d.a, d.b] : [d.b, d.a];
      const g = rowOf(left);
      if ((mask >> i) & 1) {
        // move the RIGHT partner to sit just after the LEFT one
        moveAdjacent(g, right.uid, left.uid, 1);
      } else {
        // move the LEFT partner to sit just before the RIGHT one
        moveAdjacent(g, left.uid, right.uid, -1);
      }
    });
    for (const d of defects) {
      const [left, right] = d.a.x <= d.b.x ? [d.a, d.b] : [d.b, d.a];
      for (const leaf of d.leaves) {
        const side = parkSide(leaf, d.a, d.b);
        moveAdjacent(
          rowOf(leaf),
          leaf.uid,
          side < 0 ? left.uid : right.uid,
          side
        );
      }
    }
    const order = new Map();
    for (const arr of rowsCopy.values())
      arr.forEach((uid, i) => order.set(uid, i));
    candidates.push(order);
  }
  return candidates;
};

// ——— WEDGE-PARK candidates (the proxy-wedge packed-row trap, 2026-08-12) ———
// A childless single-married couple-block (canonically a PROXY CLONE pair —
// e.g. G-8-00018's Claire-partner clone + spouse) wedged strictly INSIDE a
// ≥3-spouse hub's spouse fan flips the row packing so the refine's greedy
// hill-climb settles in a local optimum: no single candidate can both widen
// the jammed ancestor couple AND clear the wedge, so a residual dog-leg
// survives every pass — and the wedge costs a DOG, not a blocked bar, so the
// de-straddle detector above never sees it. Like the de-straddle reseed, the
// escape is deterministic rank mutations relaid out LOCKED: park the wedged
// pair at the row START and at the row END (≤2 extra layouts, one pair per
// model — lowest pid first). Adoption uses its own strictly-gated rung in
// maybeDestraddle (cross/over/lanes not worse, stretch and width bounded,
// total midrange dog-leg strictly better by ≥ 0.25·NODE_GAP), so a family
// without a genuine straightness win is byte-identical. Corpus census
// 2026-08-12: 0 of 58 fixture families detect a wedge; the live G-8-00018
// reaches ALL-STRAIGHT (u(6,7) 0.645u → 0, the 2-3 couple widening emerging
// from the relayout) as a stable fixpoint. Kill switch:
// ENABLE_WEDGE_PARK_RESEED.
export const wedgeParkCandidateOrders = (model) => {
  const persons = model.nodes.filter((n) => n.kind === config.NODE_TYPE.NODE);
  const byPid = new Map(persons.map((n) => [n.pid, n]));
  // Row/rank space is the GRID (virtualY/virtualX), not rendered px: a 3rd+
  // spouse's y is lifted off the row by extraSpouseYStep, which would
  // otherwise break both the fan grouping and the rank rows.
  // The px fallback assumes a uniform pitch, which per-generation spacing
  // breaks — but it is only reached for a node with no virtualY, and every
  // engine-built node has one (TGCLOUD-1610).
  const rowOf = (n) =>
    Math.round(n.virtualY ?? n.y / (config.LEVEL_HEIGHT || 1200));
  const vx = (n) => n.virtualX ?? n.x / (config.NODE_GAP || 800);

  // Only fire when the family actually carries a straightness cost the park
  // could buy back (max union midrange dog ≥ 0.25 grid units).
  let maxDog = 0;
  for (const u of model.nodes) {
    if (u.kind !== config.NODE_TYPE.UNION) continue;
    const xs = (u.childrenIds || [])
      .map((id) => byPid.get(id))
      .filter(Boolean)
      .map((k) => k.x);
    if (!xs.length) continue;
    const mid = (Math.min(...xs) + Math.max(...xs)) / 2;
    maxDog = Math.max(maxDog, Math.abs(u.x - mid));
  }
  if (maxDog < 0.25 * (config.NODE_GAP || 800)) return [];

  // ≥3-spouse hub fans: the hub plus its same-row spouses' x-span.
  const fans = [];
  for (const h of persons) {
    if ((h.spouseIds || []).length < 3) continue;
    const fan = [h];
    for (const sid of h.spouseIds) {
      const sp = byPid.get(sid);
      if (sp && rowOf(sp) === rowOf(h)) fan.push(sp);
    }
    if (fan.length < 3) continue;
    const xs = fan.map(vx);
    fans.push({
      row: rowOf(h),
      lo: Math.min(...xs),
      hi: Math.max(...xs),
      pids: new Set(fan.map((n) => n.pid))
    });
  }
  if (!fans.length) return [];

  // The wedge: a mutually-single-married childless pair strictly inside a fan
  // it does not belong to, on the fan's row.
  let wedge = null;
  for (const p of persons) {
    if ((p.spouseIds || []).length !== 1 || (p.childrenIds || []).length)
      continue;
    const q = byPid.get(p.spouseIds[0]);
    if (!q || q.pid <= p.pid) continue; // canonical pair order + dedupe
    if ((q.spouseIds || []).length !== 1 || q.spouseIds[0] !== p.pid) continue;
    if ((q.childrenIds || []).length) continue;
    if (rowOf(p) !== rowOf(q)) continue;
    const lo = Math.min(vx(p), vx(q));
    const hi = Math.max(vx(p), vx(q));
    const inside = fans.some(
      (f) =>
        f.row === rowOf(p) &&
        !f.pids.has(p.pid) &&
        !f.pids.has(q.pid) &&
        lo > f.lo + 1e-6 &&
        hi < f.hi - 1e-6
    );
    if (!inside) continue;
    if (!wedge || p.pid < wedge.p.pid) wedge = { p, q };
  }
  if (!wedge) return [];

  // Base rank rows (same convention as captureEstablishedOrder / the
  // de-straddle mutations): per-row uid arrays sorted by x.
  const baseRows = new Map();
  for (const n of persons) {
    const g = rowOf(n);
    let arr = baseRows.get(g);
    if (!arr) baseRows.set(g, (arr = []));
    arr.push(n);
  }
  for (const arr of baseRows.values()) arr.sort((a, b) => vx(a) - vx(b));

  const mutate = (toEnd) => {
    const order = new Map();
    for (const [g, arr] of baseRows.entries()) {
      let uids = arr.map((n) => n.uid);
      if (g === rowOf(wedge.p)) {
        const pair = arr
          .filter((n) => n.pid === wedge.p.pid || n.pid === wedge.q.pid)
          .map((n) => n.uid);
        uids = uids.filter((u) => !pair.includes(u));
        uids = toEnd ? [...uids, ...pair] : [...pair, ...uids];
      }
      uids.forEach((uid, i) => order.set(uid, i));
    }
    return order;
  };
  return [mutate(false), mutate(true)];
};

export const pedigree = ({
  family,
  familyId,
  customPositions,
  establishedOrder = null,
  orientationSeed = null,
  _seeded = false,
  _noDestraddle = false,
  _enforced = false,
  // B1a: a precomputed unlocked layout to use as the fixpoint's `free` rung instead of
  // recomputing it. maybeDestraddle passes the incumbent model here for each candidate:
  // the candidate's `free` (an unlocked pedigree over the same family/customPositions/flags)
  // is byte-identical to that incumbent — _noDestraddle only skips the post-wrap, never
  // changes the computed model — so reusing it is exact and saves a full pipeline per
  // candidate. Null (default) everywhere else, so every non-destraddle call is unchanged.
  _freeModel = null
}) => {
  // RUNAWAY GUARD (P1.4): above MAX_LAYOUT_SEARCH_SIZE the crossing-minimising reorder
  // search would run an unbounded background compute. Such families fall back to the single
  // descent-forest apportionment (no best-of-both, no bidirectional search, no refine) —
  // fast and overlap-free, flagged `simplifiedLayout`. The cap is set well above the largest
  // real corpus family (278), so no real family is ever degraded; see config.js.
  const tooBig = family.length > (config.MAX_LAYOUT_SEARCH_SIZE ?? Infinity);

  // ESTABLISHED ORDER (Phase 2, flagged): a pedigree the user has already arranged carries a
  // persisted within-generation order. When present, the engine seeds + locks it and we skip
  // best-of-both AND refinement (a reorder-capable hill-climb) — so adding/removing a relative
  // never reshuffles the existing people. Spacing/coords still recompute (clean reflow).
  const lockedOrder =
    establishedOrder && config.ENABLE_ESTABLISHED_ORDER
      ? establishedOrder
      : null;

  // D7 — AUTO-ARRANGE ORIENTATION ANCHOR: auto-arrange clears the lock so the
  // engine re-optimises from scratch, but the objective is mirror-invariant, so a
  // from-scratch run can hand back the tree with its global handedness flipped
  // (the pid seed decides). When the caller supplies the just-cleared order as an
  // orientationSeed, lay the family out BOTH ways — from scratch AND seeded from
  // the displayed arrangement (search fully free in both) — and keep the better;
  // ties go to the seeded one. A pure mirror can never strictly win (identical
  // score), so auto-arrange tidies without flipping; a genuinely better
  // arrangement still wins regardless of orientation.
  if (
    orientationSeed &&
    orientationSeed.size &&
    !_seeded &&
    !lockedOrder &&
    !tooBig
  ) {
    const fromScratch = pedigree({
      family,
      familyId,
      customPositions,
      _noDestraddle
    });
    const seeded = pedigree({
      family,
      familyId,
      customPositions,
      orientationSeed,
      _seeded: true,
      _noDestraddle
    });
    const aS = modelScore(fromScratch);
    const bS = modelScore(seeded);
    // Dogleg deadband for the orientation choice. Was ±1px when the pipeline's
    // doglegs tied exactly between mirror basins; the HUB-ASYM ANCHOR's clamped
    // junctions are NOT mirror-symmetric (a child-midpoint reachable at t=0.7 in
    // one handedness can clamp at 0.95 in the other), so small orientation-
    // asymmetric dogleg deltas now exist. Flipping the WHOLE chart's handedness
    // on auto-arrange is a predictability cost (the G-19-01458 QA complaint the
    // anchor exists for) — it must be bought by a SUBSTANTIAL straightness win:
    // at least one full grid unit of total dogleg. Crossing/over differences
    // above still always decide first.
    const DL = config.NODE_GAP || 800;
    if (aS.sum !== bS.sum) return aS.sum < bS.sum ? fromScratch : seeded;
    if (aS.over !== bS.over) return aS.over < bS.over ? fromScratch : seeded;
    // Spouse-stretch rung (G-1-01429 blind spot): a marriage bar torn over
    // EMPTY space ties every rung above. Same 2-slot deadband as the score's
    // allowance, so only a substantial tear decides the orientation.
    const SDB = 2 * (config.NODE_GAP || 800);
    if (Math.abs(aS.stretch - bS.stretch) > SDB)
      return aS.stretch < bS.stretch ? fromScratch : seeded;
    const dA = doglegScore(fromScratch);
    const dB = doglegScore(seeded);
    if (Math.abs(dA - dB) > DL) return dA < dB ? fromScratch : seeded;
    // Full tie → whichever better preserves the displayed arrangement. (The
    // seeded run usually wins here, but when the seed matches the from-scratch
    // basin the scratch result IS the arrangement — concordance 1 — so nothing
    // gratuitously moves.)
    return orientationConcordance(orientationSeed, fromScratch) >=
      orientationConcordance(orientationSeed, seeded)
      ? fromScratch
      : seeded;
  }

  // FIXPOINT FAST-PATH (D3/D4, G-19-01458): the unlocked pipeline is deterministic,
  // so a reopened, UNCHANGED pedigree re-derives the exact settle its order was
  // captured from. If that from-scratch result AGREES with the captured order (no
  // two ranked people inverted — an unranked newcomer never disagrees), adopt it
  // wholesale: full-optimiser quality AND the user's order, and reopens become
  // byte-identical to the settle. Only when a structural edit genuinely moved the
  // optimum (the from-scratch result would REORDER established people) do we pay
  // the locked pipeline, which reproduces the captured order at some quality cost.
  // Skipped for runaway-guard families (no double layout there).
  if (lockedOrder && !tooBig && !_enforced) {
    // B1a: reuse a precomputed incumbent layout when supplied (destraddle candidates),
    // else compute the unlocked `free` layout as before.
    const free =
      (config.ENABLE_LAYOUT_REDUNDANCY_ELISION !== false && _freeModel) ||
      pedigree({ family, familyId, customPositions, _noDestraddle });
    // ARRANGE BEACON grading, shared by BOTH return paths below (2026-08-12 —
    // Tim's ruling: deliberately de-optimised manual arrangements must light
    // the beacon). The reference is what Auto-arrange would actually produce:
    // the unlocked optimum with PINS CLEARED (Auto-arrange clears both maps).
    // Without pins that is exactly the `free` model already computed (no extra
    // cost, and free === returned on the fast-path ⇒ no hint — unchanged
    // behaviour). With pins, the returned model carries the pin damage on
    // both rungs of the old comparison and it cancelled — so the beacon was
    // structurally blind to pins; grading against the pin-free optimum makes
    // pin damage count. Cost: one extra pipeline per layout ONLY when pins
    // exist. Ladder: crossings+overs → overs → stretch beyond a 2-slot
    // deadband → dog-leg beyond one grid unit (a pure mirror or sub-unit
    // wiggle never counts). Gated on !_noDestraddle so destraddle candidate
    // probes never pay for or leak a hint.
    const gradeArrangeBeacon = (returned) => {
      if (!config.ENABLE_ARRANGE_BEACON || _noDestraddle) return returned;
      const havePins = customPositions && customPositions.size > 0;
      const reference = havePins
        ? pedigree({ family, familyId, customPositions: null, _noDestraddle })
        : free;
      if (reference === returned) return returned;
      const fS = modelScore(reference);
      const eS = modelScore(returned);
      const DL = config.NODE_GAP || 800;
      const SDB = 2 * (config.NODE_GAP || 800);
      if (
        fS.sum < eS.sum ||
        (fS.sum === eS.sum && fS.over < eS.over) ||
        (fS.sum === eS.sum &&
          fS.over === eS.over &&
          fS.stretch < eS.stretch - SDB) ||
        (fS.sum === eS.sum &&
          fS.over === eS.over &&
          Math.abs(fS.stretch - eS.stretch) <= SDB &&
          doglegScore(reference) < doglegScore(returned) - DL)
      ) {
        returned.arrangeHint = true;
      }
      return returned;
    };
    if (rankedSequenceAgrees(free.nodes, lockedOrder))
      return gradeArrangeBeacon(free);
    // Second rung: the SEEDED pipeline (order re-ranked, search free). When its
    // result still reproduces the saved sequence, adopt it — the saved
    // arrangement at full-optimiser geometry, deterministic given (family,
    // order), so reopens stay byte-stable. Only when the free search would MOVE
    // a ranked person do we run the locked pipeline (the `_enforced` recursion),
    // which enforces the order at some quality cost.
    const seeded = pedigree({
      family,
      familyId,
      customPositions,
      orientationSeed: lockedOrder,
      _seeded: true,
      _noDestraddle: true
    });
    const enforced = rankedSequenceAgrees(seeded.nodes, lockedOrder)
      ? seeded
      : pedigree({
          family,
          familyId,
          customPositions,
          establishedOrder: lockedOrder,
          _noDestraddle: true,
          _enforced: true
        });
    // ARRANGE BEACON: the order-preserving result is what we return — but the free
    // run above is the canonical optimum, so grade it. Strictly better on the D7
    // rungs (crossings+overs → overs → dog-leg beyond one grid unit, so a pure
    // mirror or a sub-unit wiggle never counts) ⇒ flag the model; the UI lights
    // Auto-arrange and the USER decides when to adopt the tidier arrangement.
    // Gated on !_noDestraddle so the destraddle candidate probes (which pass an
    // establishedOrder of their own) never pay for or leak a hint.
    return gradeArrangeBeacon(enforced);
  }

  const locked = lockedOrder;

  // Lay the family out once (V2 contour + crossing-refinement, or the V1 fallback) on a fresh
  // family instance and return it. A closure so best-of-both can run it twice.
  const layoutOnce = () => {
    const pf = new PedigreeFamily(familyId);
    family.forEach((personData) => pf.addPerson(personData));
    if (config.ENABLE_LAYOUT_V2)
      executeLayoutV2(
        pf,
        buildRenderModel,
        locked,
        _seeded ? orientationSeed : null
      );
    else pf.executeLayout();
    // Crossing-aware refinement (contour layout only, gated): straighten the small dog-legs
    // the apportionment leaves WITHOUT raising the real line-crossing count. No-op unless
    // ENABLE_CONTOUR_MERGE_XASSIGN is on. Skipped under the runaway guard (cost). When a
    // locked order is in force it STILL runs, in ORDER-PRESERVING mode (last arg) — the
    // straightening sweeps fire (so a locked layout's drops stay as straight as the unlocked
    // one — e.g. C1366's half-sib 48 drop), while the reordering sweeps self-reject so the
    // user's established order is never disturbed.
    if (!tooBig) {
      refineCrossingsAwareV3(pf, customPositions, buildRenderModel, !!locked);
    }
    return pf;
  };

  // NOTE: a locked order does NOT short-circuit best-of-both. The engine seeds the locked
  // order and skips only the reorder SEARCH (see LayoutV2Engine._locked), so best-of-both
  // still runs for a both-ancestried family and its dog-leg tie-break still picks the
  // straight-drop bidirectional layout — locking the order must not regress layout quality.

  // DE-STRADDLE RESEED (see destraddleCandidateOrders above): unlocked layouts
  // only — a user-locked order is never overridden, and the candidate runs mark
  // themselves _noDestraddle so the recursion is depth-bounded. Adoption is
  // strictly Pareto: fewer crossings, over and lane counts not worse; ties keep
  // the incumbent, so pattern-free (and non-improvable) families are
  // byte-identical.
  const maybeDestraddle = (model) => {
    if (_noDestraddle || locked || tooBig) return model;
    if (!config.ENABLE_LAYOUT_V2 || !config.ENABLE_CONTOUR_MERGE_XASSIGN)
      return model;
    if (config.ENABLE_DESTRADDLE_RESEED === false) return model;
    const candidateSpecs = destraddleCandidateOrders(model).map((order) => ({
      order,
      kind: 'straddle'
    }));
    // WEDGE-PARK candidates (own kind → own adoption rung below; the legacy
    // straddle gate is untouched, so pattern-free families stay byte-identical).
    if (config.ENABLE_WEDGE_PARK_RESEED !== false) {
      for (const order of wedgeParkCandidateOrders(model))
        candidateSpecs.push({ order, kind: 'wedgePark' });
    }
    // Same total cap as the straddle mask enumeration (≤4 extra layouts).
    candidateSpecs.splice(4);
    if (!candidateSpecs.length) return model;
    const personWidth = (m) => {
      const xs = m.nodes
        .filter((n) => n.kind === config.NODE_TYPE.NODE)
        .map((n) => n.x);
      return xs.length ? Math.max(...xs) - Math.min(...xs) : 0;
    };
    const cur = destraddleScore(model);
    let bestModel = model;
    let bestScore = cur;
    let adopted = false;
    for (const { order, kind } of candidateSpecs) {
      const cand = pedigree({
        family,
        familyId,
        customPositions,
        establishedOrder: order,
        _noDestraddle: true,
        // B1a: the candidate's fixpoint `free` rung is an unlocked layout over the same
        // inputs as this incumbent `model` → byte-identical; hand it over so the candidate
        // reuses it instead of recomputing a full pipeline. (When the incumbent agrees with
        // `order`, the fixpoint returns it and the candidate is exactly the incumbent — the
        // common no-op case the profile showed dominating.)
        _freeModel: model
      });
      const cs = destraddleScore(cand);
      const better =
        kind === 'straddle' &&
        cs.cross < bestScore.cross &&
        cs.over <= bestScore.over &&
        cs.lanes <= bestScore.lanes;
      const tieBreak =
        kind === 'straddle' &&
        adopted &&
        cs.cross === bestScore.cross &&
        cs.over === bestScore.over &&
        cs.lanes === bestScore.lanes &&
        doglegScore(cand) < doglegScore(bestModel) - 1e-6;
      // WEDGE-PARK rung: a park never buys a crossing win (the wedge costs a
      // dog, not a lane), so it must pay with STRAIGHTNESS — total midrange
      // dog-leg strictly better by ≥ 0.25 grid units — while crossings, overs,
      // lanes never worsen and marriage stretch + chart width stay within a
      // 2-slot allowance (the emergent couple widening is the accepted
      // spread-don't-dogleg trade; a park that only relocates the problem
      // fails the dog rung and is refused).
      const DLW = config.NODE_GAP || 800;
      const wedgeParkBetter =
        kind === 'wedgePark' &&
        cs.cross <= bestScore.cross &&
        cs.over <= bestScore.over &&
        cs.lanes <= bestScore.lanes &&
        spouseStretchScore(cand) <= spouseStretchScore(bestModel) + 2 * DLW &&
        personWidth(cand) <= personWidth(bestModel) + 2 * DLW &&
        doglegScore(cand) < doglegScore(bestModel) - 0.25 * DLW;
      if (better || tieBreak || wedgeParkBetter) {
        bestModel = cand;
        bestScore = cs;
        adopted = true;
      }
    }
    return bestModel;
  };

  // Runaway fallback: force the descent-forest (bidir OFF → _contourApportion, no reorder
  // search) for an oversized family, then flag the model so the UI can prompt a manual nudge
  // or a proxy. Returns before best-of-both so the expensive double layout never runs.
  if (
    tooBig &&
    config.ENABLE_LAYOUT_V2 &&
    config.ENABLE_CONTOUR_MERGE_XASSIGN
  ) {
    const prevBidir = config.ENABLE_BIDIRECTIONAL_CONTOUR;
    config.ENABLE_BIDIRECTIONAL_CONTOUR = false;
    let model;
    try {
      model = buildRenderModel(layoutOnce(), customPositions);
    } finally {
      config.ENABLE_BIDIRECTIONAL_CONTOUR = prevBidir;
    }
    model.simplifiedLayout = true;
    return model;
  }

  // BEST-OF-BOTH (contour path only): when a family has a both-ancestried couple — the case the
  // bidirectional contour is built for — lay it out BOTH ways (descent-forest AND bidirectional)
  // through the FULL pipeline, then keep whichever has fewer hop-overs (crossings + marriage-
  // over-node), preferring fewer marriage-over-node on a tie (a parallel line reads as badly as a
  // crossing — Tim). This never regresses a family below the shipped descent-forest (it is kept
  // unless the bidir strictly wins), so every golden holds, and the bidir is adopted only where it
  // genuinely reduces hop-overs (G-1-01395/01396). Other families lay out once (the bidir can't
  // help them), so there is no double-layout cost for the common case.
  if (
    config.ENABLE_LAYOUT_V2 &&
    config.ENABLE_CONTOUR_MERGE_XASSIGN &&
    config.ENABLE_BIDIRECTIONAL_CONTOUR &&
    hasBothAncestriedCouple(family)
  ) {
    const prev = config.ENABLE_BIDIRECTIONAL_CONTOUR;
    config.ENABLE_BIDIRECTIONAL_CONTOUR = false;
    const dfPf = layoutOnce();
    // B1b: when the bidirectional contour is unsupported for this family (containment
    // bridge / diverged layout / cross-gen couple — recorded by runContourLayoutV3), the
    // bidir run would take the identical `_contourApportion` fallback, so its full layout
    // (placement + refine, both flag-independent) is byte-identical to the descent-forest
    // run just computed. Reuse it instead of paying a second full pipeline. The predicate
    // is computed the same way in both runs from flag-independent state, so this never
    // skips a run that would have differed.
    let biPf;
    if (
      config.ENABLE_LAYOUT_REDUNDANCY_ELISION !== false &&
      dfPf._bidirUnsupportedV3
    ) {
      biPf = dfPf;
    } else {
      config.ENABLE_BIDIRECTIONAL_CONTOUR = true;
      biPf = layoutOnce();
    }
    config.ENABLE_BIDIRECTIONAL_CONTOUR = prev;
    const dfModel = buildRenderModel(dfPf, customPositions);
    const biModel =
      biPf === dfPf ? dfModel : buildRenderModel(biPf, customPositions);
    const dfS = hopoverScore(dfModel, dfPf);
    const biS = hopoverScore(biModel, biPf);
    // Tie-break ladder (lower wins): (1) hop-overs total, (2) fewer marriage-over-node,
    // (3) fewer dog-legs. The dog-leg tie-break fires ONLY when crossings AND
    // marriage-over-node are both equal, so on a simple both-ancestried family that ties
    // 0/0 we now adopt the straight-drop bidirectional layout instead of the descent-
    // forest's offset one (the G-1-01398 grievance) — without any risk of reintroducing a
    // crossing or parallel line, since either would have broken the tie above. A 1px
    // deadband (childPath's own straight-drop threshold) ignores sub-pixel noise so the
    // descent-forest is still kept when the two are geometrically equivalent.
    const DL_EPS = 1;
    // NOTE: the spouse-stretch term (beacon + D7 orientation anchor) is
    // deliberately NOT a rung here. Best-of-both picks GEOMETRY, and the
    // fully-ancestried stress families carry legitimate wide bars the
    // own-sibship exemption doesn't fully model — adding the rung flipped
    // G-1-01391's winner and lost its dead-straight u(18-19) drop. The known
    // tear producer (G-1-01429 residual duplicate leaf) is fixed at source;
    // if a torn candidate ever wins here again, revisit with a
    // containment-aware exemption instead of loosening the deadband.
    const biBetter =
      biS.sum < dfS.sum ||
      (biS.sum === dfS.sum && biS.over < dfS.over) ||
      (biS.sum === dfS.sum &&
        biS.over === dfS.over &&
        doglegScore(biModel) + DL_EPS < doglegScore(dfModel));
    return maybeDestraddle(biBetter ? biModel : dfModel);
  }

  return maybeDestraddle(buildRenderModel(layoutOnce(), customPositions));
};
